import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowUpRight, Filter, SlidersHorizontal } from "lucide-react";
import { PracticeModeModal } from "@/components/PracticeModeModal";
import { getPersonaPhoto } from "@/lib/personaPhoto";

const DIFF_CONFIG = {
  easy: { bg: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-400" },
  medium: { bg: "bg-orange-50 text-orange-800 border-orange-200", dot: "bg-orange-400" },
  hard: { bg: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-400" },
};

export default function Scenarios() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scenarios, setScenarios] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [audience, setAudience] = useState("All");
  const [startingId, setStartingId] = useState(null);
  const [practiceScenario, setPracticeScenario] = useState(null); // scenario awaiting mode selection

  useEffect(() => {
    Promise.all([
      api.get("/scenarios"),
      api.get("/taxonomy/audiences"),
    ])
      .then(([sc, ad]) => {
        setScenarios(sc.data);
        setAudiences(ad.data);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(scenarios.map((s) => s.category));
    return ["All", ...Array.from(set)];
  }, [scenarios]);

  const visible = useMemo(() => {
    return scenarios.filter((s) => {
      if (category !== "All" && s.category !== category) return false;
      if (audience !== "All" && !(s.audience || []).includes(audience)) return false;
      return true;
    });
  }, [scenarios, category, audience]);

  const startSession = async (scenario, mode) => {
    setStartingId(scenario.id);
    try {
      const res = await api.post("/sessions", { scenario_id: scenario.id });
      navigate(`/session/${res.data.id}`, { state: { mode } });
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error(
          err.response.data?.detail ||
            "You've reached your practice session limit. Please contact your facilitator.",
          { duration: 8000 },
        );
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setStartingId(null);
    }
  };

  // Open the practice mode selection modal before creating a session
  const handlePracticeClick = (scenario) => {
    setPracticeScenario(scenario);
  };

  const audienceLabel = (key) =>
    (audiences.find((a) => a.key === key) || {}).label || key;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-500/15 blur-3xl" aria-hidden />
        <div className="absolute bottom-0 left-10 w-80 h-80 rounded-full bg-blue-600/10 blur-3xl" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-12 md:py-16">
          <div className="text-xs font-semibold uppercase tracking-widest text-orange-400 mb-3">Practice library</div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight font-bold max-w-2xl">
            Pick a difficult conversation.
          </h1>
          <p className="mt-3 text-slate-400 max-w-xl">
            Real manager moments — burnout, feedback, flight risks, succession.
            Pick what feels uncomfortable — that's the one to practice.
          </p>
          <div className="mt-5 flex items-center gap-3 text-sm text-slate-400">
            <span className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1">
              <span className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
              {loading ? "…" : `${scenarios.length} scenarios available`}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-8">

        {/* Filter row */}
        <div className="mb-8" data-testid="scenarios-filters">
          <div className="flex items-center gap-2 mb-3 text-sm text-slate-500">
            <SlidersHorizontal className="w-4 h-4 text-blue-500" strokeWidth={1.75} />
            <span className="font-medium">Filter by topic</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-btn`}
                onClick={() => setCategory(c)}
                className={`px-4 py-1.5 rounded-full text-sm border font-medium transition-all ${
                  category === c
                    ? "bg-[#EA580C] text-white border-[#EA580C] shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:border-orange-300 hover:text-orange-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-12 text-slate-500 text-sm text-center py-20">Loading scenarios…</div>
        ) : visible.length === 0 ? (
          <div className="mt-12 bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            No scenarios match that filter. Try widening the topic.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((s, i) => {
              const diffConfig = DIFF_CONFIG[s.difficulty] || DIFF_CONFIG.medium;
              return (
                <article
                  key={s.id}
                  data-testid={`scenario-card-${s.slug}`}
                  className="group flex flex-col bg-white border border-slate-200 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:border-orange-200 hover:-translate-y-0.5 slide-up-fade"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between mb-4 gap-2">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                      {s.category}
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${diffConfig.bg}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${diffConfig.dot}`} />
                      {s.difficulty}
                    </div>
                  </div>

                  {/* Audience tags */}
                  {(s.audience || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(s.audience || []).map((a) => (
                        <span
                          key={a}
                          className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                        >
                          {audienceLabel(a)}
                        </span>
                      ))}
                    </div>
                  )}

                  <h3 className="font-display text-xl font-bold text-slate-900 leading-snug">{s.title}</h3>
                  <div className="mt-2.5 flex items-center gap-3">
                    <img
                      src={getPersonaPhoto(s.persona_name, s.persona_gender)}
                      alt={s.persona_name}
                      loading="lazy"
                      data-testid={`scenario-persona-photo-${s.slug}`}
                      className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow-sm border border-slate-100 shrink-0"
                    />
                    <div className="min-w-0 text-sm text-slate-500">
                      <div className="font-semibold text-slate-700 truncate">{s.persona_name}</div>
                      <div className="truncate text-xs">{s.persona_role}</div>
                    </div>
                  </div>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed flex-1">{s.description}</p>

                  {/* Skills */}
                  {(s.coaching_skills || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-2">Skills covered</div>
                      <div className="flex flex-wrap gap-1.5">
                        {s.coaching_skills.map((sk) => (
                          <span
                            key={sk}
                            data-testid="scenario-skill-pill"
                            className="text-xs px-2.5 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-200"
                          >
                            {sk}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-400 italic flex-1 min-w-0 truncate">{s.persona_mood}</div>
                    <Button
                      data-testid={`start-${s.slug}-btn`}
                      disabled={startingId === s.id}
                      onClick={() => handlePracticeClick(s)}
                      className="rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 h-10 shrink-0 font-semibold shadow-sm shadow-orange-200"
                    >
                      {startingId === s.id ? "Starting…" : (
                        <>Practice <ArrowUpRight className="w-4 h-4 ml-1" strokeWidth={2} /></>
                      )}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Practice Mode Selection Modal */}
      {practiceScenario && (
        <PracticeModeModal
          scenario={practiceScenario}
          loading={startingId === practiceScenario.id}
          onConfirm={(mode) => {
            startSession(practiceScenario, mode);
          }}
          onClose={() => {
            if (!startingId) setPracticeScenario(null);
          }}
        />
      )}
    </div>
  );
}
